"use client";

import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const specs = [{
  id: "spec-01",
  number: "01",
  eyebrow: "ENGENHARIA DE PRECISÃO",
  headline: "Desafios Comuns no Processamento de Laticínios",
  body: "Os processadores de laticínios enfrentam uma variedade de desafios que podem afetar a qualidade do produto, a eficiência operacional e os objetivos de sustentabilidade:\n\nProdução Inconsistente de Pó: Variações na secagem por pulverização podem conduzir a tamanho de partícula, teor de humidade e densidade aparente inconsistentes nos produtos de leite em pó.\nDesperdício de Inibidores de Bolores: A sobredosagem de inibidores de bolores não só aumenta os custos como também cria desperdício desnecessário.\nLimpeza de Tanques Intensiva em Água: Os métodos de limpeza tradicionais utilizam frequentemente quantidades excessivas de água, aumentando os custos operacionais e o impacto ambiental.",
  linkLabel: "Conheça a nossa tecnologia",
  image: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-05-as-17.00.07.png?v=pe3yi9q86zd",
  imageAlt: "Sistema de pulverização industrial de alta precisão em ambiente fabril",
  imageLeft: true
}, {
  id: "spec-02",
  number: "02",
  eyebrow: "PARCERIA EXCLUSIVA",
  headline: "As Nossas Soluções para o Processamento de Laticínios",
  body: "A Spraying Systems Co. orgulha-se de oferecer soluções avançadas de tecnologia de pulverização concebidas especificamente para a indústria de laticínios. O nosso equipamento inovador e a vasta expertise ajudam-no a superar os desafios comuns, permitindo:\n\nGarantir uma qualidade consistente do pó com controlo sobre o tamanho de partícula, a humidade e a densidade.\nMinimizar o desperdício e reforçar a segurança alimentar com a aplicação precisa de inibidores de bolores.\nReduzir o consumo de água e otimizar os processos de limpeza no local (CIP).\nCumprir normas rigorosas de higiene e segurança, mantendo a eficiência operacional.\nAceder a apoio especializado para garantir que o seu equipamento funciona no máximo desempenho.\n",
  linkLabel: "Veja as nossas marcas",
  image: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-05-as-17.00.14.png?v=mrlxzgmib8l",
  imageAlt: "Equipamento de pulverização industrial com foco em componentes de precisão",
  imageLeft: false
}, {
  id: "spec-03",
  number: "03",
  eyebrow: "SUPORTE ESPECIALIZADO",
  headline: "Soluções de Secagem por Pulverização para um Pó Perfeito em Todas as Situações",
  body: "A qualidade dos produtos de leite em pó depende do controlo preciso do tamanho de partícula, do teor de humidade e da densidade aparente. Variações na secagem por pulverização podem conduzir a inconsistência e desperdício. Os bicos SprayDry® proporcionam a precisão necessária para manter uma qualidade consistente do pó, garantindo que os seus produtos cumprem os mais elevados padrões e reduzindo o desperdício de produção.\nCaracterísticas Principais:\n\nControlo preciso do tamanho de partícula e do teor de humidade.\nDensidade aparente consistente para produtos em pó de elevada qualidade.\nRedução do desperdício e melhoria da eficiência de produção.",
  linkLabel: "Ver Produtos SprayDry®\n",
  image: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-05-as-17.00.19.png?v=fv2txq6poy7",
  imageAlt: "Consultoria técnica em ambiente industrial com foco em otimização",
  imageLeft: true,
  link: {
    href: "https://www.spray.com/-/media/dam/industrial/usa/sales-material/product-market-bulletin/b695c_spraydry_bulletin.pdf",
    newTab: true
  }
}];
export default function Engineering() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".eng-opener", {
        y: 24
      });
      gsap.to(".eng-opener", {
        opacity: 1,
        y: 0,
        duration: 0.62,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".eng-opener-wrap",
          start: "top 82%",
          once: true
        }
      });
      const rows = el.querySelectorAll<HTMLElement>(".eng-row");
      rows.forEach(row => {
        const numberEl = row.querySelector<HTMLElement>(".eng-number");
        const targetNumber = numberEl?.dataset.target ?? "01";
        const image = row.querySelector<HTMLElement>(".eng-image");
        const copy = row.querySelectorAll<HTMLElement>(".eng-copy");
        gsap.set(image, {
          y: 16
        });
        gsap.set(copy, {
          y: 20
        });
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: row,
            start: "top 78%",
            once: true
          }
        });
        if (numberEl) {
          const counter = {
            val: 0
          };
          const end = parseInt(targetNumber, 10);
          tl.to(counter, {
            val: end,
            duration: 0.48,
            ease: "power2.out",
            onUpdate: () => {
              numberEl.textContent = String(Math.round(counter.val)).padStart(2, "0");
            }
          }, 0);
        }
        tl.to(image, {
          opacity: 1,
          y: 0,
          duration: 0.62,
          ease: "power3.out"
        }, 0);
        tl.to(copy, {
          opacity: 1,
          y: 0,
          duration: 0.56,
          ease: "power3.out",
          stagger: 0.08
        }, 0.08);
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".eng-opener", ".eng-image", ".eng-copy"], {
        opacity: 1,
        y: 0
      });
      el.querySelectorAll<HTMLElement>(".eng-number").forEach(n => {
        n.textContent = n.dataset.target ?? "01";
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="31131" id="spaciouscontentshowcase" data-nav="dark" className="bg-background text-foreground py-[clamp(112px,14vw,200px)] overflow-hidden">
      <div ref={container} className="mx-auto w-full max-w-[1360px] px-[clamp(20px,4vw,56px)]">
        {}
        <div className="eng-opener-wrap mx-auto max-w-3xl text-center">
          <div className="eng-opener text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground" style={{
          opacity: 0
        }}>
            Engenharia
          </div>
          <h2 className="eng-opener mt-6 font-default font-medium text-foreground" style={{
          opacity: 0,
          fontSize: "clamp(36px, 4.8vw, 76px)",
          letterSpacing: "-0.03em",
          lineHeight: 1.02
        }}>Tecnologia de Pulverização para Laticínios</h2>
          <p className="eng-opener mx-auto mt-8 max-w-xl text-muted-foreground" style={{
          opacity: 0,
          fontSize: "clamp(17px, 1.15vw, 19px)",
          lineHeight: 1.55
        }}>Na Spraying Systems Co., especializamo-nos em fornecer soluções de pulverização inovadoras adaptadas às necessidades únicas da indústria de laticínios. Quer pretenda melhorar a qualidade de produtos de leite em pó, reforçar a segurança alimentar ou reduzir o consumo de água na limpeza de tanques, a nossa vasta experiência garante que podemos disponibilizar os bicos, cabeçotes de pulverização e sistemas automatizados adequados às suas operações.</p>
        </div>

        {}
        <div className="mt-[clamp(80px,10vw,140px)] space-y-[clamp(96px,12vw,160px)]">
          {specs.map((spec, i) => <article key={spec.id} data-index={i} id={spec.id} className="eng-row grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-x-8 lg:gap-x-12 items-center scroll-mt-24">
              {}
              <div className={`md:col-span-6 ${spec.imageLeft ? "md:order-1" : "md:order-2"}`}>
                <div className="eng-image relative overflow-hidden rounded-3xl border border-border aspect-[4/5] bg-accent" style={{
              opacity: 0,
              boxShadow: "var(--shadow-lg)"
            }}>
                  <img src={spec.image} alt={spec.imageAlt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
                  {}
                  <div className="pointer-events-none absolute inset-0 rounded-3xl" style={{
                boxShadow: "inset 0 1px 0 hsl(0 0% 100% / 0.1)"
              }} />
                </div>
              </div>

              {}
              <div className={`md:col-span-6 ${spec.imageLeft ? "md:order-2" : "md:order-1"}`}>
                <div className="eng-copy eng-number font-default font-normal tabular-nums text-primary" data-target={spec.number} style={{
              opacity: 0,
              fontSize: "clamp(48px, 4.2vw, 72px)",
              letterSpacing: "-0.04em",
              lineHeight: 0.9
            }}>
                  00
                </div>

                <div className="eng-copy mt-6 text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground" style={{
              opacity: 0
            }}>
                  {spec.eyebrow}
                </div>

                <h3 className="eng-copy mt-4 font-default font-medium text-foreground" style={{
              opacity: 0,
              fontSize: "clamp(28px, 2.8vw, 44px)",
              letterSpacing: "-0.025em",
              lineHeight: 1.08
            }}>
                  {spec.headline}
                </h3>

                <p className="eng-copy mt-6 max-w-xl text-muted-foreground" style={{
              opacity: 0,
              fontSize: "clamp(17px, 1.15vw, 19px)",
              lineHeight: 1.55
            }}>
                  {spec.body}
                </p>

                <div className="eng-copy mt-8" style={{
              opacity: 0
            }}>
                  <Button asChild variant="link" className="group h-auto p-0 text-foreground hover:no-underline">
                    <Link to="/ContactsPage">
                      <span className="relative inline-flex items-center gap-2">
                        <Link className="relative underline cursor-pointer" {...spec.link}><span>
                          {spec.linkLabel}
                          <span className="absolute left-0 -bottom-0.5 block h-px w-0 bg-primary transition-all duration-[260ms] ease-out group-hover:w-full" />
                        </span></Link>
                        <ArrowRight className="size-4 transition-transform duration-[260ms] ease-out group-hover:translate-x-1" />
                      </span>
                    </Link>
                  </Button>
                </div>
              </div>
            </article>)}
        </div>
      </div>
    </section>;
}